<template>
  <Drawer
    v-model="composingEmail"
    title="Send Results via Email"
    placement="right"
    width="720"
    :closable="true"
    :mask-closable="false"
    class="drawer-class"
    @on-close="onDrawerClose"
  >
    <Form
      ref="formValidate"
      :model="formData"
      :rules="formRule"
      :styles="styles"
    >
      <Row>
        <span style="float: right;">
          <Tooltip
            v-show="!showCc"
            content="Add Cc recipients"
            placement="bottom"
          >
            <Button
              class="email-action"
              @click="clearEmail('cc')"
            >
              Cc
            </Button>
          </Tooltip>
          <Tooltip
            v-show="!showBcc"
            content="Add Bcc recipients"
            placement="bottom"
          >
            <Button
              class="email-action"
              @click="clearEmail('bcc')"
            >
              Bcc
            </Button>
          </Tooltip>
        </span>
      </Row>
      <Row :gutter="32">
        <i-col span="24">
          <FormItem prop="to">
            <Select
              v-model="formData.to"
              placeholder="To"
              multiple
              filterable
            >
              <Option
                v-for="user in users"
                :key="user.email"
                :label="user.name"
                :value="user.email"
              />
            </Select>
          </FormItem>
        </i-col>
      </Row>
      <Row :gutter="32">
        <i-col span="24">
          <FormItem
            v-if="showCc"
            prop="cc"
          >
            <Select
              v-model="formData.cc"
              placeholder="Cc"
              multiple
              filterable
            >
              <Option
                v-for="user in users"
                :key="user.email"
                :label="user.name"
                :value="user.email"
              />
            </Select>
          </FormItem>
        </i-col>
      </Row>
      <Row :gutter="32">
        <i-col span="24">
          <FormItem
            v-if="showBcc"
            prop="bcc"
          >
            <Select
              v-model="formData.bcc"
              placeholder="Bcc"
              multiple
              filterable
            >
              <Option
                v-for="user in users"
                :key="user.email"
                :label="user.name"
                :value="user.email"
              />
            </Select>
          </FormItem>
        </i-col>
      </Row>
      <Row :gutter="32">
        <i-col span="24">
          <FormItem prop="subject">
            <Input
              v-model="formData.subject"
              placeholder="Subject"
            />
          </FormItem>
        </i-col>
      </Row>
      <Row :gutter="32">
        <i-col span="24">
          <FormItem prop="content">
            <ContentEditor
              ref="contentEditor"
              v-model="formData.content"
              editable
            />
            <div>* Query result will be sent with the content</div>
          </FormItem>
        </i-col>
      </Row>
    </Form>
    <div class="drawer-footer">
      <Button
        type="primary"
        @click="validateEmailForm"
      >
        Send
      </Button>
    </div>
  </Drawer>
</template>

<script>
import { Base64 } from 'js-base64'
import _ from 'lodash'
import ContentEditor from './forum/ContentEditor'
import UserMixin from '@/mixins/UserMixin'

const DEFAULT_EDITOR_CONTENT = {
  JSON: {
    content: {}
  },
  HTML: '',
  IMAGES: null,
  ATTACHMENTS: null
}

export default {
  components: {
    ContentEditor
  },

  mixins: [
    UserMixin
  ],

  props: {
    queryResults: Array,
    queryName: String,
    colTitleArr: Array,
    isComposingEmail: Boolean,
    context: String
  },

  data () {
    return {
      composingEmail: false,
      showCc: true,
      showBcc: false,
      formData: {
        to: [],
        cc: [ this.$auth.getUser().email ],
        bcc: [],
        subject: `Results from ${this.queryName}`,
        content: _.cloneDeep(DEFAULT_EDITOR_CONTENT),
        data: this.convertDataToHTML(this.queryResults)
      },
      formRule: {
        to: [
          { type: 'array', required: true, message: 'Recipient cannot be empty', trigger: 'change' }
        ],
        subject: [
          { required: true, message: 'Subject cannot be empty', trigger: 'blur' }
        ]
      },
      styles: {
        height: 'calc(100% - 55px)',
        overflow: 'auto',
        paddingBottom: '53px',
        position: 'static'
      }
    }
  },

  watch: {
    queryName (val) {
      this.formData.subject = `Results from ${val}`
    },

    isComposingEmail (val) {
      this.composingEmail = val
    }
  },

  created () {
    this.fetchAllUsers()
  },

  methods: {
    /**
     * This converts table data to Google Sheet API complied format
     * @param {Array} data Represents the table data from query.
     * @return {String} Converted HTML snippet
     */
    convertDataToHTML (data) {
      const header = this.colTitleArr.map(title => {
        return '<th style="border: 1px solid rgb(232,234,236);' +
            'box-sizing: border-box;' +
            'margin: 0px;' +
            'padding: 0 18px;' +
            'min-width: 0px;' +
            'height: 40px;' +
            'text-align: center;' +
            'text-overflow: ellipsis;' +
            'vertical-align: middle;' +
            'white-space: nowrap;' +
            'overflow: hidden;' +
            `background-color: rgb(248, 248, 249);">${title}</th>`
      }).join('')
      const body = data.map(row => {
        const tmpRow = this.colTitleArr.map(title => {
          return `<td align="center" valign="top" style="border: 1px solid rgb(232, 234, 236); padding: 0 18px;">${row[title]}</td>`
        }).join('')
        return `<tr>${tmpRow}</tr>`
      }).join('')

      return `<p></p><table cellpadding="0" cellspacing="0" height="100%" width="100%" align="center"><tr>${header}</tr>${body}</table>`
    },

    /**
     * Validate email form
     */
    validateEmailForm () {
      this.$refs.formValidate.validate((valid) => {
        valid === true ? this.sendEmail() : this.onSheetCreationError('From Validation Failed')
      })
    },

    /**
     * Clear email address for Cc and Bcc
     * @param {String} type either 'cc' or 'bcc'
     */
    clearEmail (type) {
      switch (type) {
        case 'cc':
          this.showCc = !this.showCc
          this.formData.cc = []
          break
        case 'bcc':
          this.showBcc = !this.showBcc
          this.formData.bcc = []
          break
      }
    },

    /**
     * Clean the editor content after drawer close
     */
    clearEditor () {
      this.$refs.contentEditor.editor.clearContent()
      this.formData.content = _.cloneDeep(DEFAULT_EDITOR_CONTENT)
    },

    /**
     * Send result via email
     */
    async sendEmail () {
      try {
        const gapi = await this.$getGapiClient()
        if (!this.$isAuthenticated()) {
          this.$refreshToken()
        }
        if (!await gapi.auth2.getAuthInstance().currentUser.get().isSignedIn()) {
          await this.$login()
        }

        const to = this.formData.to.join()
        const cc = this.formData.cc.join()
        const bcc = this.formData.bcc.join()

        const email = [
          `From: ${gapi.auth2.getAuthInstance().currentUser.get().getBasicProfile().getEmail()}`,
          `To: ${to}`,
          `Cc: ${cc}`,
          `Bcc: ${bcc}`,
          'MIME-Version: 1.0',
          `Subject: ${this.formData.subject}`,
          'Content-Type: text/html; charset=utf-8',
          '',
          this.formData.content.HTML,
          '',
          this.convertDataToHTML(this.queryResults)
        ]
        const base64EncodedEmail = Base64.encodeURI(email.join('\n'))
        const request = gapi.client.gmail.users.messages.send({
          userId: 'me',
          resource: {
            raw: base64EncodedEmail
          }
        })
        request.then(
          () => this.onEmailSentSuccess(this.formData.subject, to, cc, bcc),
          (error) => this.onEmailSentError(error.result.error.message)
        )
      } catch (error) {
        let errorMessage
        if (error.error) {
          errorMessage = error.error
        } else if (error.result && error.result.error) {
          errorMessage = error.result.error.message
        } else {
          errorMessage = error
        }
        return this.onEmailSentError(errorMessage)
      }
    },

    /**
     * Callback function to show email sent success
     * @param {String} subject Represents email subject
     * @param {String} to Represents email 'to' recipients
     * @param {String} cc Represents email 'cc' recipients
     * @param {String} bcc Represents email 'bcc' recipients
     */
    onEmailSentSuccess (subject, to, cc, bcc) {
      this.$refs.formValidate.resetFields()
      this.$log('email_send', this.query, { to: to, cc: cc, bcc: bcc, subject: subject })
      this.$Message.success({
        content: 'Email sent successfully',
        duration: 5
      })
      this.onDrawerClose()
    },

    /**
     * Displays a pop up to show email sent error
     * @param {String} error represents error message to display.
     */
    onEmailSentError (error) {
      this.$Message.error({
        content: `Error while sending email because ${error}`,
        duration: 8
      })
    },

    /**
     * Emit composingEmail when user closes drawer
     */
    onDrawerClose () {
      this.to = ''
      this.cc = ''
      this.bcc = ''
      this.formData.cc = [ this.$auth.getUser().email ]
      this.clearEditor()
      this.showCc = true
      this.showBcc = false
      this.composingEmail = false
      this.$emit('isComposingEmail', this.composingEmail)
    }
  }
}
</script>

<style scoped>
  .drawer-class >>> .ivu-drawer-mask{
    z-index: 2000 !important;
  }

  .drawer-class >>> .ivu-drawer-wrap {
    z-index: 2000 !important;
  }

  .ivu-form-item-error >>> .ti-input {
    border: 1px solid var(--color-red-border);
  }

  .ivu-form-item-error >>> .ck-editor {
    border: 1px solid var(--color-red-border);
    border-radius: 4px;
  }

  .email-action {
    cursor: pointer;
    user-select: none;
    border: none;
    padding: 0;
  }

  .email-action:hover {
    text-decoration: underline;
  }

  .drawer-footer{
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    border-top: 1px solid #e8e8e8;
    padding: 10px 16px;
    text-align: right;
    background: #fff;
  }

  .ck-editor {
    border-radius: 4px;
  }
</style>
